export default function Orders({ orders }) {
  const safeOrders = Array.isArray(orders) ? orders : [];
  const activeStatuses = new Set(["QUEUED", "PROCESSING", "SUBMITTED", "RECONCILIATION_REQUIRED"]);

  const localDateKey = (value) => {
    if (!value) return "";
    const isoDate = String(value).match(/^\d{4}-\d{2}-\d{2}/)?.[0];
    if (isoDate) return isoDate;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
  };

  const tradeDateValue = (order) => (
    order?.trading_date || order?.created_at || order?.completed_at || order?.updated_at
  );

  const today = new Date();
  const previousDay = new Date(today);
  previousDay.setDate(today.getDate() - 1);
  const retainedTradeDates = new Set([localDateKey(today), localDateKey(previousDay)]);

  const orderTime = (order) => {
    const value = order?.updated_at || order?.completed_at || order?.created_at;
    const parsed = value ? Date.parse(value) : NaN;

    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const formatTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  };

  const formatTradeDate = (value) => {
    const key = localDateKey(value);
    if (!key) return "Date unavailable";
    const [year, month, day] = key.split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const displayOrders = safeOrders.filter((order) => (
    activeStatuses.has((order.status || "").toUpperCase())
      || retainedTradeDates.has(localDateKey(tradeDateValue(order)))
  )).sort((a, b) => {
    const aActive = activeStatuses.has((a.status || "").toUpperCase());
    const bActive = activeStatuses.has((b.status || "").toUpperCase());

    if (aActive !== bActive) return aActive ? -1 : 1;

    const timeDiff = orderTime(b) - orderTime(a);
    if (timeDiff !== 0) return timeDiff;

    return (b.order_sequence || 0) - (a.order_sequence || 0);
  });

  const getStatusColor = (status) => {
    switch ((status || "").toUpperCase()) {
      case "PLACED":
      case "DUMMY":
        return "#16c784";
      case "FAILED":
        return "#ea3943";
      case "PROCESSING":
      case "SUBMITTED":
      case "RECONCILIATION_REQUIRED":
        return "#f7931a";
      case "QUEUED":
      case "SKIPPED":
        return "#888";
      default:
        return "#555";
    }
  };

  const displayStatus = (status, isSquareOff = false) => {
    if (String(status || "").toUpperCase() === "RECONCILIATION_REQUIRED") {
      return isSquareOff ? "EXIT PENDING AT DHAN" : "DHAN ORDER PENDING";
    }
    return status;
  };

  const getSideColor = (side) => {
    return side === "BUY" ? "#16c784" : "#ea3943";
  };

  const getClientReason = (c) => {
    return (
      c?.error ||
      c?.reason ||
      c?.broker_status ||
      c?.status_message ||
      "-"
    );
  };

  return (
    <div
      className="orders-tree"
      style={{
        height: "100%",
        minHeight: 0,
        overflowY: "auto",
        border: "1px solid #2a2a2a",
        padding: "10px",
        borderRadius: "8px",
        background: "#111",
        color: "#eaeaea"
      }}
    >
      {displayOrders.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No orders</p>
      ) : (
        displayOrders.map((o) => (
          <div
            key={o.order_id}
            className="orders-tree-order"
            style={{
              marginTop: "8px",
              padding: "8px 10px",
              borderRadius: "8px",
              background: "#1a1a1a",
              border: "1px solid #2a2a2a"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: "8px", minWidth: 0 }}>
                <strong style={{ fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.tradingsymbol}</strong>
                <span style={{ color: "#94a3b8", fontSize: "10px", whiteSpace: "nowrap" }}>
                  {formatTradeDate(tradeDateValue(o))}
                </span>
              </div>

              <span
                style={{
                  background: getStatusColor(o.status),
                  color: "#000",
                  padding: "2px 7px",
                  borderRadius: "6px",
                  fontSize: "10px",
                  fontWeight: "bold"
                }}
              >
                {displayStatus(o.status, o.is_square_off)}
              </span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "4px",
                fontSize: "11px",
                opacity: 0.8
              }}
            >
              <span>{o.order_id}</span>

              <span style={{ color: getSideColor(o.side), fontWeight: "bold" }}>
                {o.side} {o.qty}
              </span>
            </div>

            <div
              style={{
                marginTop: "4px",
                fontSize: "11px",
                opacity: 0.65
              }}
            >
              {o.is_square_off ? "Close" : "Open"}: {formatTime(o.updated_at || o.created_at)}
            </div>

            {/* Client-level order results */}
            {Array.isArray(o.clients) && o.clients.length > 0 && (
              <div
                className="orders-tree-clients"
                style={{
                  marginTop: "8px",
                  borderTop: "1px solid #2a2a2a",
                  paddingTop: "6px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px"
                }}
              >
                {o.clients.map((c, idx) => (
                  <div
                    key={`${o.order_id}-${c.client_id || idx}`}
                    className="orders-tree-client"
                    style={{
                      background: "#111",
                      border: "1px solid #2a2a2a",
                      borderRadius: "6px",
                      padding: "7px 8px",
                      fontSize: "11px"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <strong>{c.client_id}</strong>

                      <span
                        style={{
                          background: getStatusColor(c.status),
                          color: "#000",
                          padding: "2px 6px",
                          borderRadius: "5px",
                          fontSize: "10px",
                          fontWeight: "bold"
                        }}
                      >
                        {displayStatus(c.status, o.is_square_off)}
                      </span>
                    </div>

                    <div style={{ marginTop: "4px", opacity: 0.8 }}>
                      Qty: {c.qty || "-"} | Order ID: {c.order_id || "-"}
                    </div>

                    <div style={{ marginTop: "4px", opacity: 0.8 }}>
                      Broker: {c.broker || "-"} | IP: {c.execution_ip_group || "-"} | Latency: {c.latency_ms ? `${Math.round(c.latency_ms)} ms` : "-"}
                    </div>

                    {getClientReason(c) !== "-" && (
                      <div
                        style={{
                          marginTop: "5px",
                          color: c.status === "FAILED" ? "#ff6b6b" : "#facc15",
                          wordBreak: "break-word"
                        }}
                      >
                        Reason: {getClientReason(c)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
